import {
    Box,
    IconButton,
    Icons,
    Stack,
    Text,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import ActivityStepContinueButton from "components/selection-components/select-activity/activity-step-continue-button";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getUniqueResourceArticlesByTimeAndGroup } from "utils";
interface IActivityFooter {
    setSummaryOpen: (value: boolean) => void;
    gotoDetailsForm: () => void;
}

/**
 * The total height of the fixed ActivityFooter on mobile.
 * Derived from: button size "xl" (56px, see casper-ui-library button-theme.js) +
 * the footer's top and bottom padding (ThemeSpaceVariable.Medium each).
 * Used by the activity list container to add bottom padding so the last item
 * is not obscured by the fixed footer.
 */
const ACTIVITY_FOOTER_HEIGHT = `calc(56px + ${ThemeSpaceVariable.Medium} * 2)`;

const ActivityFooter = ({ setSummaryOpen, gotoDetailsForm }: IActivityFooter) => {
    const { t } = useTranslation();
    const { data: reservation } = useWebBooking();
    const footerLabel = t("selectActivity.continueOrEditFooterName");
    const uniqueResourceGroups = useMemo(
        () => (reservation ? getUniqueResourceArticlesByTimeAndGroup(reservation) : []),
        [reservation]
    );
    const selectedActivities = uniqueResourceGroups?.length ?? 0;
    const selectedActivitiesQuantity = selectedActivities < 10 ? String(selectedActivities) : "9+";

    return (
        <Box
            bg="transparent"
            position={{ base: "fixed", md: "static" }}
            bottom={{ base: 0, md: "auto" }}
            right={{ base: 0, md: "auto" }}
            left={{ base: 0, md: "auto" }}
            pt={{ base: ThemeSpaceVariable.Medium, md: ThemeSpaceVariable.None }}
            px={{ base: ThemeSpaceVariable.Medium, md: ThemeSpaceVariable.None }}
            pb={{ base: ThemeSpaceVariable.Medium, md: ThemeSpaceVariable.None }}
            mt={{ md: ThemeSpaceVariable.Medium }}
            zIndex={1}
            aria-label={footerLabel}
            role="navigation"
            display={{ base: selectedActivities > 0 ? "flex" : "none", lg: "none" }}
        >
            <Stack flexDirection="row" gap={ThemeFontSizeVariable.Medium} w="full">
                <Box alignSelf="flex-end" w="full" borderRadius="full" minW="0" flex="1">
                    <ActivityStepContinueButton onClick={gotoDetailsForm} />
                </Box>
                <Box position="relative" borderRadius="full" alignSelf="flex-end" flexShrink="0">
                    <IconButton
                        aria-label={t("selectActivity.changeSelection")}
                        icon={Icons.Cart}
                        size="xl"
                        onClick={() => {
                            setSummaryOpen(true);
                        }}
                        variant="primary"
                    />
                    <Box
                        position="absolute"
                        top="-1"
                        right="-1"
                        borderRadius={ThemeFontSizeVariable.XLarge}
                        bg={ThemeColorVariable.Secondary}
                        width={ThemeFontSizeVariable.XLarge}
                        height={ThemeFontSizeVariable.XLarge}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Text color={ThemeColorVariable.OnSecondary} fontWeight="500">
                            {selectedActivitiesQuantity}
                        </Text>
                    </Box>
                </Box>
            </Stack>
        </Box>
    );
};

export { ACTIVITY_FOOTER_HEIGHT };

export default ActivityFooter;
